import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as bookingApi from "./bookingApi";
import { WhereFact } from "./WhereStep";
import { DeskHeader, DeskPicker, slotLine, useWide } from "./desktop";
import { clock, dayKey, longDay, offsetName, whenLabel } from "./format";
import {
  AgendaChoices,
  AgendaLines,
  Divider,
  Header,
  Mono,
  GuestBox,
  NoteBox,
  Page,
  PrimaryButton,
  SecondaryButton,
  Skeleton,
  SlotPicker,
  TextLink,
  ZonePicker,
} from "./parts";

/*
 * /book/manage/:token — the link in the calendar invite.
 *
 *   D5  move (the default view): the call, what it's for, what they asked, and
 *       a picker for the new time. The agenda and note move with the call.
 *   D6  cancel: "Life happens", with an optional check-in.
 *   S5  already cancelled   S6  started or past   S7  link not found
 */

export function LinkNotFound() {
  const navigate = useNavigate();
  return (
    <Page bar="YOUR CALL WITH GABRIEL">
      <div className="bk-body bk-body--top">
        <p className="bk-hmm">Hmm.</p>
        <h1 className="bk-title">This link doesn't match a call.</h1>
        <p className="bk-lede">
          It may be from an older invite. The latest one from Google has the right link. Or just book fresh.
        </p>
        <PrimaryButton onClick={() => navigate("/book")}>Book a time</PrimaryButton>
      </div>
    </Page>
  );
}

function firstName(name) {
  return String(name || "").trim().split(/\s+/)[0] || "there";
}

export default function ManagePage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [zone, setZone] = useState(null);
  const [view, setView] = useState("move"); // move | cancel
  const [day, setDay] = useState(null);
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [editingFor, setEditingFor] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const [editingGuests, setEditingGuests] = useState(false);
  const [checkIn, setCheckIn] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const wide = useWide();

  const load = useCallback(
    async (tz) => {
      try {
        const next = await bookingApi.getManage({ token, tz });
        setData(next);
        setZone((z) => z || next.timezone);
        setError(null);
        return next;
      } catch (err) {
        setError(err);
        return null;
      }
    },
    [token]
  );

  useEffect(() => {
    load(null);
  }, [load]);

  // "Started 4 min ago" stays true while the page is open.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  if (error && error.status === 404) return <LinkNotFound />;
  if (error) {
    return (
      <Page>
        <div className="bk-body bk-body--top">
          <Mono className="bk-alert-kicker">● Booking is down for a moment</Mono>
          <h1 className="bk-title">Hey. Our calendar hiccuped.</h1>
          <p className="bk-lede">Your call is still booked. Try again in a minute.</p>
          <PrimaryButton onClick={() => load(zone)}>Try again</PrimaryButton>
        </div>
      </Page>
    );
  }
  if (!data) {
    return (
      <Page>
        <Header kicker="Remotion" />
        <div className="bk-body">
          <Skeleton />
        </div>
      </Page>
    );
  }

  const { booking, company, agendas } = data;
  const tz = zone || booking.timezone;
  const who = [company ? `${company} × Remotion` : "Remotion", booking.name].filter(Boolean).join(" · ").toUpperCase();
  // A second call's agenda was written from the first call, so there is none to pick.
  const agenda = booking.follow_up ? booking.agenda : agendas && agendas.find((a) => a.key === booking.intent);
  const canChoose = !booking.follow_up && agendas && agendas.length > 0;
  const start = new Date(booking.start).getTime();
  const end = new Date(booking.end).getTime();

  const saveAgenda = async (fields) => {
    const next = await bookingApi.updateAgenda({ token, ...fields });
    setData((d) => ({ ...d, booking: next }));
  };

  const guestNames = (booking.guests || []).map((g) => g.name || g.email).join(", ");
  const guestsFact = (
    <>
      <dt className="bk-mono">With</dt>
      <dd>
        <div className="bk-fact-head">
          {editingGuests ? null : <span>{guestNames || "Just you"}</span>}
          <TextLink onClick={() => setEditingGuests((v) => !v)}>{editingGuests ? "Done" : "Add someone"}</TextLink>
        </div>
        {editingGuests ? (
          <GuestBox
            stacked
            guests={booking.guests || []}
            onAdd={async (email) => {
              const next = await bookingApi.addGuest({ token, email });
              setData((d) => ({ ...d, booking: next }));
            }}
            onRemove={async (email) => {
              const next = await bookingApi.removeGuest({ token, email });
              setData((d) => ({ ...d, booking: next }));
            }}
          />
        ) : null}
      </dd>
    </>
  );

  /* ---------- S5: cancelled ---------- */
  if (booking.status === "cancelled") {
    return (
      <Page bar="YOUR CALL WITH GABRIEL">
        <div className="bk-body bk-body--top bk-split">
          <div className="bk-split__main">
          <p className="bk-struck bk-mono">{whenLabel(booking.start, booking.end, tz)} ({offsetName(tz)})</p>
          <h1 className="bk-title">Hey {firstName(booking.name)}. This call was cancelled.</h1>
          <p className="bk-lede">
            {agenda || booking.note
              ? "Your agenda is still saved. Pick a new time and it comes with you."
              : "Pick a new time whenever suits you."}
          </p>
          {booking.check_in_at ? (
            <p className="bk-muted">
              I'll check back in around {longDay(`${booking.check_in_at}T12:00:00Z`, "UTC")}.
            </p>
          ) : null}
          <PrimaryButton
            onClick={() =>
              navigate(data.invite ? `/book?c=${encodeURIComponent(data.invite)}` : "/book", {
                state: {
                  carry: { name: booking.name, email: booking.email, intent: booking.intent, note: booking.note },
                },
              })
            }
          >
            Book a new time
          </PrimaryButton>
          </div>
          {wide && (agenda || booking.note) ? (
            <div className="bk-card bk-card--quiet bk-split__side">
              <Mono>Saved</Mono>
              {agenda ? <p className="bk-strong">{agenda.label}</p> : null}
              {booking.note ? <p>"{booking.note}"</p> : null}
            </div>
          ) : null}
        </div>
      </Page>
    );
  }

  /* ---------- S6: started or past ---------- */
  if (now >= start) {
    const live = now < end;
    const minutes = Math.max(1, Math.round((now - start) / 60000));
    if (wide) {
      return (
        <Page bar="YOUR CALL WITH GABRIEL">
          <div className="bk-body bk-body--top bk-split">
            <div className="bk-split__main">
              {live ? (
                <Mono className="bk-live">● Started {minutes} min ago</Mono>
              ) : (
                <p className="bk-struck bk-mono">
                  {whenLabel(booking.start, booking.end, tz)} ({offsetName(tz)})
                </p>
              )}
              <h1 className="bk-title">{live ? `We're on now, ${firstName(booking.name)}.` : "This call is in the past."}</h1>
              <p className="bk-lede">
                {live
                  ? "It's too late to move or cancel this one. Running late? Just join when you can."
                  : `Thanks for your time, ${firstName(booking.name)}. It can't be moved or cancelled now. Want another one?`}
              </p>
              <div className="bk-inline">
                {live && booking.meet_url ? (
                  <>
                    <a className="bk-primary bk-primary--link bk-primary--fit" href={booking.meet_url} target="_blank" rel="noreferrer">
                      Join the Meet
                    </a>
                    <a className="bk-link" href={booking.meet_url} target="_blank" rel="noreferrer">
                      {booking.meet_url.replace(/^https?:\/\//, "")}
                    </a>
                  </>
                ) : (
                  <PrimaryButton className="bk-primary bk-primary--fit" onClick={() => navigate("/book")}>
                    Book another call
                  </PrimaryButton>
                )}
              </div>
            </div>
          </div>
        </Page>
      );
    }
    return (
      <Page>
        {live ? (
          <header className="bk-header bk-header--green">
            <Mono className="bk-kicker">● Started {minutes} min ago</Mono>
            <h1 className="bk-headline">We're on now, {firstName(booking.name)}.</h1>
          </header>
        ) : (
          <Header kicker={who}>This call is in the past.</Header>
        )}
        <div className="bk-body">
          {live && booking.meet_url ? (
            <a className="bk-primary bk-primary--link" href={booking.meet_url} target="_blank" rel="noreferrer">
              Join the Meet
            </a>
          ) : null}
          <p className="bk-lede">
            {live
              ? "It's too late to move or cancel this one. Running late? Just join when you can."
              : "Want another one?"}
          </p>
          {!live ? <PrimaryButton onClick={() => navigate("/book")}>Book again</PrimaryButton> : null}
        </div>
      </Page>
    );
  }

  /* ---------- D6: cancel ---------- */
  if (view === "cancel") {
    const doCancel = async () => {
      setBusy(true);
      setActionError(null);
      try {
        const next = await bookingApi.cancel({ token, checkIn });
        setData((d) => ({ ...d, booking: next }));
      } catch (err) {
        setActionError(err.message);
      } finally {
        setBusy(false);
      }
    };
    const saved = agenda || booking.note;
    return (
      <Page bar="YOUR CALL WITH GABRIEL">
        <div className={`bk-body bk-body--top ${wide ? "bk-split" : "bk-body--fill"}`}>
          <div className="bk-split__main">
          <h1 className="bk-title bk-title--xl">
            Cancel? All good.
            <br />
            Life happens.
          </h1>
          <p className="bk-lede">
            I'll free up {longDay(booking.start, tz)} and Google will send the cancellation.
            {agenda || booking.note ? " Your agenda and note stay saved at this link." : ""}
          </p>
          {!wide && saved ? (
            <div className="bk-card bk-card--quiet">
              <Mono>Saved{agenda ? ` · ${agenda.label}` : ""}</Mono>
              {booking.note ? <p>You asked: "{booking.note.length > 60 ? `${booking.note.slice(0, 57)}…` : booking.note}"</p> : null}
            </div>
          ) : null}
          <div className="bk-card">
            <p className="bk-strong">Want me to check back in? Optional.</p>
            <div className="bk-pills" role="radiogroup" aria-label="Check back in">
              {[
                ["2w", "Try again in 2 weeks"],
                ["1m", "In a month"],
                ["no", "No thanks"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={checkIn === key}
                  className="bk-pill"
                  onClick={() => setCheckIn(checkIn === key ? null : key)}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="bk-muted">Pick nothing and you won't hear from me about it.</p>
          </div>
          <div className={wide ? "bk-inline" : "bk-stack"}>
            {actionError ? <p className="bk-error">{actionError}</p> : null}
            <PrimaryButton busy={busy} onClick={doCancel} className={`bk-primary${wide ? " bk-primary--fit" : ""}`}>
              Cancel the call
            </PrimaryButton>
            <SecondaryButton onClick={() => setView("move")} className={`bk-secondary${wide ? " bk-secondary--fit" : ""}`}>
              Move it instead
            </SecondaryButton>
          </div>
          </div>
          {wide ? (
            <div className="bk-card bk-card--quiet bk-split__side">
              <Mono>The call you're cancelling</Mono>
              <p className="bk-strong">
                {whenLabel(booking.start, booking.end, tz)} ({offsetName(tz)})
              </p>
              {agenda ? (
                <p>
                  <b>For:</b> {agenda.label}
                </p>
              ) : null}
              {booking.note ? (
                <p>
                  <b>You asked:</b> "{booking.note}"
                </p>
              ) : null}
              {saved ? <p className="bk-muted">All of this stays saved if you book again.</p> : null}
            </div>
          ) : null}
        </div>
      </Page>
    );
  }

  /* ---------- D5: move ---------- */
  const doMove = async () => {
    if (!slot) return;
    setBusy(true);
    setActionError(null);
    try {
      const next = await bookingApi.reschedule({ token, start: slot.start, tz });
      navigate(`/book/booked/${token}`, {
        replace: false,
        // `type` as the page knows it (name, length), not the bare key the move returns.
        state: { booking: { ...next, type: data.type, company, agendas, before_we_talk: data.before_we_talk || [] }, moved: true },
      });
    } catch (err) {
      if (err.code === "slot_taken") {
        setActionError(`Someone just grabbed ${clock(slot.start, tz)}. Pick another time.`);
        setSlot(null);
        load(tz);
      } else {
        setActionError(err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const facts = (
    <>
      <dl className="bk-facts">
        <dt className="bk-mono">Was</dt>
        <dd className={slot ? "bk-struck" : ""}>
          {whenLabel(booking.start, booking.end, tz)} ({offsetName(tz)})
        </dd>
        <WhereFact location={booking.location} host={data.host} />
        {booking.meet_url ? (
          <>
            <dt className="bk-mono">Meet</dt>
            <dd>
              <a className="bk-meet" href={booking.meet_url} target="_blank" rel="noreferrer">
                {booking.meet_url.replace(/^https?:\/\//, "")}
              </a>
            </dd>
          </>
        ) : null}
        <dt className="bk-mono">For</dt>
        <dd>
          <div className="bk-fact-head">
            <strong>{agenda ? agenda.label : "Not chosen yet"}</strong>
            {canChoose ? <TextLink onClick={() => setEditingFor((v) => !v)}>{editingFor ? "Done" : agenda ? "Change" : "Choose"}</TextLink> : null}
          </div>
          {editingFor ? (
            <AgendaChoices agendas={agendas} value={booking.intent} onChange={(key) => saveAgenda({ intent: key })} />
          ) : null}
          {agenda ? <AgendaLines agenda={agenda} /> : null}
        </dd>
        <dt className="bk-mono">Asked</dt>
        <dd>
          {editingNote ? null : <span>{booking.note ? `"${booking.note}"` : "Nothing yet"}</span>}{" "}
          <TextLink onClick={() => setEditingNote((v) => !v)}>{editingNote ? "Done" : booking.note ? "Edit" : "Add"}</TextLink>
          {editingNote ? (
            <NoteBox
              value={booking.note}
              autoFocus
              placeholder="A question, a goal for the call, a link to an ad you want me to look at"
              onSave={(note) => saveAgenda({ note })}
            />
          ) : null}
        </dd>
        {guestsFact}
      </dl>
    </>
  );

  /* DESKTOP (Remotion_Booking_-_Desktop.pptx, 4A): the call on the left, the new-time picker on the right. */
  if (wide) {
    return (
      <Page>
        <DeskHeader right="MANAGE YOUR CALL" kicker={who}>
          Hey {firstName(booking.name)}. Move the call?
        </DeskHeader>
        <div className="bkd-body bkd-cols bkd-cols--manage">
          <section className="bkd-callbox">
            <span className="bk-mono bkd-callbox__head">Your call</span>
            <div className="bkd-callbox__facts">{facts}</div>
            <div className="bkd-callbox__foot">
              <TextLink onClick={() => setView("cancel")}>I need to cancel instead</TextLink>
            </div>
          </section>
          {data.slots.length ? (
            <DeskPicker
              label="New time"
              zone={tz}
              onZone={(next) => {
                setZone(next);
                setSlot(null);
                setDay(null);
                load(next);
              }}
              slots={data.slots}
              days={data.days}
              day={day}
              onDay={(k) => {
                setDay(k);
                setSlot(null);
              }}
              selected={slot && slot.start}
              onSelect={(s) => {
                setSlot(s);
                setDay(dayKey(s.start, tz));
              }}
              panel={(s) => (
                <div className="bkd-form">
                  <span className="bk-mono">{slotLine(s, tz)}</span>
                  {actionError ? <p className="bk-error">{actionError}</p> : null}
                  <PrimaryButton busy={busy} onClick={doMove} className="bk-primary bk-primary--fit">
                    Move to {longDay(s.start, tz)} · {clock(s.start, tz)}
                  </PrimaryButton>
                  <p className="bk-muted">Your agenda and your question move with the call. Google sends the new invite.</p>
                </div>
              )}
            />
          ) : (
            <section className="bkd-picker">
              <p className="bk-lede bkd-picker__empty">No other open times in the next two weeks. Your call stays where it is.</p>
            </section>
          )}
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <Header kicker={who}>Hey {firstName(booking.name)}. Move the call?</Header>
      <div className="bk-body">
        <dl className="bk-facts">
          <dt className="bk-mono">Was</dt>
          <dd className={slot ? "bk-struck" : ""}>
            {whenLabel(booking.start, booking.end, tz)} ({offsetName(tz)})
          </dd>
          <WhereFact location={booking.location} host={data.host} />
          {booking.meet_url ? (
            <>
              <dt className="bk-mono">Meet</dt>
              <dd>
                <a className="bk-meet" href={booking.meet_url} target="_blank" rel="noreferrer">
                  {booking.meet_url.replace(/^https?:\/\//, "")}
                </a>
              </dd>
            </>
          ) : null}
        </dl>
        <Divider />

        <dl className="bk-facts">
          <dt className="bk-mono">For</dt>
          <dd>
            <div className="bk-fact-head">
              <strong>{agenda ? agenda.label : "Not chosen yet"}</strong>
              {canChoose ? <TextLink onClick={() => setEditingFor((v) => !v)}>{editingFor ? "Done" : agenda ? "Change" : "Choose"}</TextLink> : null}
            </div>
            {editingFor ? (
              <AgendaChoices agendas={agendas} value={booking.intent} onChange={(key) => saveAgenda({ intent: key })} />
            ) : null}
            {agenda ? <AgendaLines agenda={agenda} compact /> : null}
          </dd>
          <dt className="bk-mono">Asked</dt>
          <dd>
            <div className="bk-fact-head">
              {editingNote ? null : <span>{booking.note ? `"${booking.note}"` : "Nothing yet"}</span>}
              <TextLink onClick={() => setEditingNote((v) => !v)}>{editingNote ? "Done" : booking.note ? "Edit" : "Add"}</TextLink>
            </div>
            {editingNote ? (
              <NoteBox
                value={booking.note}
                autoFocus
                placeholder="A question, a goal for the call, a link to an ad you want me to look at"
                onSave={(note) => saveAgenda({ note })}
              />
            ) : null}
          </dd>
          {guestsFact}
        </dl>
        <Divider />

        <div className="bk-row">
          <Mono>New when</Mono>
          <ZonePicker
            zone={tz}
            onChange={(next) => {
              setZone(next);
              setSlot(null);
              setDay(null);
              load(next);
            }}
          />
        </div>
        {data.slots.length ? (
          <SlotPicker
            slots={data.slots}
            zone={tz}
            day={day}
            onDay={setDay}
            selected={slot && slot.start}
            onSelect={(s) => {
              setSlot(s);
              setDay(dayKey(s.start, tz));
            }}
          />
        ) : (
          <p className="bk-lede">No other open times in the next two weeks. Your call stays where it is.</p>
        )}

        {actionError ? <p className="bk-error">{actionError}</p> : null}
        <PrimaryButton busy={busy} disabled={!slot} onClick={doMove}>
          {slot ? `Move to ${longDay(slot.start, tz)} · ${clock(slot.start, tz)}` : "Pick a new time"}
        </PrimaryButton>
        <p className="bk-fine">Your agenda and your note move with the call.</p>
        <TextLink className="bk-link bk-link--center" onClick={() => setView("cancel")}>
          Cancel instead
        </TextLink>
      </div>
    </Page>
  );
}
